import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'
import { Notification } from '../../components/NotificationPopup/NotificationPopup.types'
import {
  NotificationState,
  FilterPeriod,
  ReminderType,
} from './notifications.types'
import dayjs from 'lib/dayjs'
import { ProcedureStatus } from 'common/types'

export const useNotificationsStore = create<NotificationState>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial State
        notifications: [],
        isNotificationPopupOpen: false,
        isLoading: false,
        filterPeriod: '7days',

        // Actions
        setNotifications: (notifications: Notification[]) => {
          set({ notifications })
        },
        // UI Actions
        openNotificationPopup: () => {
          set({ isNotificationPopupOpen: true })
        },

        closeNotificationPopup: () => {
          set({ isNotificationPopupOpen: false })
        },

        toggleNotificationPopup: () => {
          set((state: NotificationState) => ({
            isNotificationPopupOpen: !state.isNotificationPopupOpen,
          }))
        },

        setLoading: (loading: boolean) => {
          set({ isLoading: loading })
        },

        setFilterPeriod: (period: FilterPeriod) => {
          set({ filterPeriod: period })
        },

        // Computed/Filtered Data
        getUnreadCount: () => {
          const { notifications } = get()
          return notifications.filter((n: Notification) => !n.isRead).length
        },

        getDateRange: () => {
          const { filterPeriod } = get()
          const now = dayjs()
          switch (filterPeriod) {
            case 'today':
              return {
                before: now.endOf('day').toDate(),
                after: now.startOf('day').toDate(),
              }
            case '7days':
              return {
                before: now.endOf('day').toDate(),
                after: now.subtract(7, 'day').startOf('day').toDate(),
              }
            default:
              return {
                before: now.endOf('day').toDate(),
                after: now.startOf('day').toDate(),
              }
          }
        },

        // Surgery State
        surgeryStatus: 'SCANNING',
        isSurgeryReminderModalOpen: false,
        reminderType: 'insideCase',

        // Surgery Actions
        setSurgeryStatus: (status: ProcedureStatus['name']) => {
          set({ surgeryStatus: status })
        },
        openSurgeryReminderModal: (reminderType: ReminderType) => {
          return new Promise<boolean>((resolve) => {
            set({
              isSurgeryReminderModalOpen: true,
              reminderType,
              onSurgeryReminderConfirm: () => {
                set({ isSurgeryReminderModalOpen: false })
                resolve(true)
              },
              onSurgeryReminderCancel: () => {
                set({ isSurgeryReminderModalOpen: false })
                resolve(false)
              },
            })
          })
        },
        closeSurgeryReminderModal: () => {
          set({ isSurgeryReminderModalOpen: false })
        },
        setReminderType: (type: ReminderType) => {
          set({ reminderType: type })
        },
      }),
      {
        name: 'notifications-storage',
        partialize: (state) => ({
          notifications: state.notifications,
          filterPeriod: state.filterPeriod,
          surgeryStatus: state.surgeryStatus,
          reminderType: state.reminderType,
        }),
      }
    ),
    {
      name: 'notifications-store',
    }
  )
)

// Selector hooks for better performance
export const useNotifications = () =>
  useNotificationsStore((state: NotificationState) => state.notifications)
export const useNotificationCount = () =>
  useNotificationsStore(
    (state: NotificationState) => state.notifications.length
  )
export const useUnreadCount = () =>
  useNotificationsStore((state: NotificationState) => state.getUnreadCount())
export const useDateRange = () => {
  const store = useNotificationsStore()
  return store.getDateRange()
}
export const useNotificationPopupOpen = () =>
  useNotificationsStore(
    (state: NotificationState) => state.isNotificationPopupOpen
  )
export const useNotificationLoading = () =>
  useNotificationsStore((state: NotificationState) => state.isLoading)
export const useFilterPeriod = () => {
  const store = useNotificationsStore()
  return store.filterPeriod
}

// Action hooks
export const useNotificationActions = () => {
  const store = useNotificationsStore()
  return {
    setNotifications: store.setNotifications,
    openNotificationPopup: store.openNotificationPopup,
    closeNotificationPopup: store.closeNotificationPopup,
    toggleNotificationPopup: store.toggleNotificationPopup,
    setLoading: store.setLoading,
    setFilterPeriod: store.setFilterPeriod,
  }
}

// Surgery state hooks
export const useSurgeryStatus = () =>
  useNotificationsStore((state: NotificationState) => state.surgeryStatus)
export const useIsSurgeryReminderModalOpen = () =>
  useNotificationsStore(
    (state: NotificationState) => state.isSurgeryReminderModalOpen
  )
export const useReminderType = () =>
  useNotificationsStore((state: NotificationState) => state.reminderType)

// Surgery action hooks
export const useSurgeryActions = () => {
  const store = useNotificationsStore()
  return {
    setSurgeryStatus: store.setSurgeryStatus,
    openSurgeryReminderModal: store.openSurgeryReminderModal,
    closeSurgeryReminderModal: store.closeSurgeryReminderModal,
    setReminderType: store.setReminderType,
    onSurgeryReminderConfirm: store.onSurgeryReminderConfirm,
    onSurgeryReminderCancel: store.onSurgeryReminderCancel,
  }
}
